import React, { FC, useRef } from "react";
import { ConfirmationDialog } from "../../../../../components/ConfirmationDialog/ConfirmationDialog";
import type { PdpUrlVideoLink } from "../../../../../components/PdpPages/PdpJsonLdOutput";
import { formatVideoName } from "../../../videos/hooks/useFetchVideos";
import type { PdpUrl } from "./pageModel";

/**
 * The section's two confirmations, copied from the modal (:872-891): Delete
 * page and Remove from page. The section owns the ask, the write and where
 * focus goes after; these draw the dialog. `busy` = that item's call is out:
 * the dialog is `disabled`, so a double click sends one DELETE (the modal's
 * button has none).
 *
 * Each keeps saying its last ask through the exit fade: `ask` is null as
 * soon as the dialog starts to close.
 */

/** The last non-null `ask` — what a closing dialog keeps showing. */
function useLastAsk<T>(ask: T | null): T | null {
  const last = useRef(ask);
  if (ask) last.current = ask;
  return last.current;
}

interface DeletePageConfirmProps {
  /** The page to delete, with its tab label as it was when asked; null = closed. */
  ask: { row: PdpUrl; label: string } | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/** Names the page by its tab's label. */
export const DeletePageConfirm: FC<DeletePageConfirmProps> = ({ ask, busy, onClose, onConfirm }) => {
  const label = useLastAsk(ask)?.label || "";
  return (
    <ConfirmationDialog
      open={!!ask}
      onClose={onClose}
      onConfirm={onConfirm}
      title={label ? `Delete the ${label} page?` : "Delete PDP URL & SKUs?"}
      description="This permanently removes the page, its SKUs, its scoring history and any generated JSON-LD. This action cannot be undone."
      confirmLabel={busy ? "Deleting…" : "Delete"}
      cancelLabel="Cancel"
      maxWidth="xs"
      disabled={busy}
    />
  );
};

interface RemoveVideoConfirmProps {
  /** The video to take off the page; null = closed. */
  ask: { row: PdpUrl; link: PdpUrlVideoLink } | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const RemoveVideoConfirm: FC<RemoveVideoConfirmProps> = ({ ask, busy, onClose, onConfirm }) => {
  const video = useLastAsk(ask)?.link.videoFile;
  return (
    <ConfirmationDialog
      open={!!ask}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Remove this video from the page?"
      description={`${
        formatVideoName(video?.name || video?.key) || "This video"
      } will no longer be associated with this page, and its Video Object JSON-LD for the page is discarded. The page, its SKUs, its score and any other videos on it stay. You can add it back from the video's settings.`}
      confirmLabel={busy ? "Removing…" : "Remove"}
      cancelLabel="Cancel"
      maxWidth="xs"
      disabled={busy}
    />
  );
};
